/**
 * The small set of interface glyphs the shell needs. One component, one
 * lookup, so nav, buttons and the mobile tab bar all pull from one place.
 */

export type UiIconName =
  | 'play'
  | 'collection'
  | 'store'
  | 'profile'
  | 'help'
  | 'gear'
  | 'close'
  | 'chevronLeft'
  | 'chevronRight'
  | 'chevronDown'
  | 'log'
  | 'copy'
  | 'flag'
  | 'lock'
  | 'deck'
  | 'dust'
  | 'hand'
  | 'shield'
  | 'sword'
  | 'check'
  | 'info';

const PATHS: Record<UiIconName, string[]> = {
  play: ['M7 4.6 19 12 7 19.4z'],
  collection: ['M3.6 6.4h7.2v11.2H3.6z', 'M13.2 6.4h7.2v11.2h-7.2z'],
  store: ['M4 9.2h16l-1.2 9.6H5.2z', 'M8.4 9.2V7a3.6 3.6 0 0 1 7.2 0v2.2'],
  profile: ['M12 4.4a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6z', 'M4.8 19.6a7.2 7.2 0 0 1 14.4 0'],
  help: ['M12 3.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8z', 'M9.6 9.6a2.4 2.4 0 1 1 3.3 2.2c-.6.3-.9.8-.9 1.5v.5', 'M12 17h.01'],
  gear: [
    'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
    'M19.2 12c0-.5 0-1-.1-1.4l1.8-1.3-1.8-3.1-2.1.8a7.4 7.4 0 0 0-2.4-1.4L14.2 3H9.8l-.4 2.6c-.9.3-1.7.8-2.4 1.4l-2.1-.8-1.8 3.1 1.8 1.3a8 8 0 0 0 0 2.8l-1.8 1.3 1.8 3.1 2.1-.8c.7.6 1.5 1.1 2.4 1.4l.4 2.6h4.4l.4-2.6c.9-.3 1.7-.8 2.4-1.4l2.1.8 1.8-3.1-1.8-1.3c.1-.4.1-.9.1-1.4z',
  ],
  close: ['M6 6 18 18', 'M18 6 6 18'],
  chevronLeft: ['M14.6 5.6 8 12l6.6 6.4'],
  chevronRight: ['M9.4 5.6 16 12l-6.6 6.4'],
  chevronDown: ['M5.6 9.4 12 16l6.4-6.6'],
  log: ['M5 4.6h14v14.8H5z', 'M8.2 9h7.6', 'M8.2 12.4h7.6', 'M8.2 15.8h4.6'],
  copy: ['M8.6 8.6h10.8v10.8H8.6z', 'M15.4 5.4H4.6v10.8'],
  flag: ['M6 3.6v17', 'M6 5h11.6l-2 3.4 2 3.4H6z'],
  lock: ['M6.2 10.8h11.6v8.4H6.2z', 'M8.6 10.8V8a3.4 3.4 0 0 1 6.8 0v2.8'],
  deck: ['M6 7.2h12v12.4H6z', 'M8.4 4.4h11.2v12.4'],
  dust: ['M6.4 8.2h11.2l-1 11.4H7.4z', 'M4.8 8.2h14.4', 'M10 5h4'],
  hand: ['M4.4 15.6 8.8 6.8', 'M9.4 17.6 12 6', 'M14.6 17.6 19.6 8.4'],
  shield: ['M12 3.4 19 6v6.2c0 4-2.9 7-7 8.4-4.1-1.4-7-4.4-7-8.4V6z'],
  sword: ['M19.6 4.4 10 14', 'M15.4 4.4h4.2v4.2', 'M4.4 19.6l5.2-5.2', 'M4.4 15.4v4.2h4.2'],
  check: ['M5 12.6 10 17.6 19.4 7.2'],
  info: ['M12 3.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8z', 'M12 11v6', 'M12 7.6h.01'],
};

const FILLED: UiIconName[] = ['play'];

export interface UiIconProps {
  name: UiIconName;
  size?: number;
  className?: string;
  title?: string;
}

export function UiIcon({ name, size = 18, className, title }: UiIconProps) {
  const paths = PATHS[name];
  const filled = FILLED.includes(name);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}
